import { cacheManager } from "@stores/cache/CacheManager";
import { createCacheMetadata } from "@stores/cache/utils";
import { IAuthService } from "../../platform/services/auth/AuthService";
import { CacheMetadata } from "@stores/cache/types";

export interface PersistenceContext<T, D> {
    getModelName: () => string;
    getSyncHash: () => string;
    getAuthService: () => IAuthService;
    getServerFilterHash: () => string;
    mapServerResponseToLocalRow: (row: T) => D;
}

export interface PersistenceLoadResult<D> {
    items: D[];
    metadata: CacheMetadata;
}

export class DataSourcePersistence<T, D> {
    private context: PersistenceContext<T, D>;
    private _cacheEnabled: boolean = true;
    private _currentUserId?: string;
    private _pendingInvalidation: Promise<void> = Promise.resolve();
    
    public hasDataFromCache: boolean = false;
    public rawServerData: T[] = [];

    constructor(context: PersistenceContext<T, D>) {
        this.context = context;
    }

    private async getCurrentUserId(): Promise<string | undefined> {
        if (!this._currentUserId) {
            const user = await this.context.getAuthService().getCurrentUser();
            this._currentUserId = user?.sub;
        }
        return this._currentUserId;
    }

    private getCacheKey(userId: string): string {
        const serverFilterHash = this.context.getServerFilterHash();
        const syncHash = this.context.getSyncHash();
        const key = `${userId}_${this.context.getModelName()}_${syncHash}_${serverFilterHash}`;
        return key;
    }

    private async waitForPendingInvalidation(): Promise<void> {
        await this._pendingInvalidation;
    }

    public async load(): Promise<PersistenceLoadResult<D> | null> {
        if (!this._cacheEnabled) return null;
        try {
            await this.waitForPendingInvalidation();
            const userId = await this.getCurrentUserId();
            if (!userId) return null;

            const cacheKey = this.getCacheKey(userId);
            const cachedData = await cacheManager.get<T[]>(cacheKey);
            if (cachedData && cachedData.data) {
                this.hasDataFromCache = true;
                this.rawServerData = [...cachedData.data];
                return {
                    items: cachedData.data.map(item => this.context.mapServerResponseToLocalRow(item)),
                    metadata: cachedData.metadata
                };
            }
            this.hasDataFromCache = false;
            this.rawServerData = [];
            return null;
        } catch (error) {
            console.warn('Failed to load from cache:', error);
            return null;
        }
    }

    public async save(rawServerData: T[], nextToken?: string | null, isPartialData: boolean = false): Promise<void> {
        if (!this._cacheEnabled) return;
        try {
            await this.waitForPendingInvalidation();
            const userId = await this.getCurrentUserId();
            if (!userId) return;

            const metadata = createCacheMetadata(
                userId,
                this.context.getSyncHash(),
                this.context.getServerFilterHash(),
                this.context.getModelName(),
                isPartialData,
                nextToken
            );
            await cacheManager.set(this.getCacheKey(userId), rawServerData, metadata);
            this.hasDataFromCache = false;
        } catch (error) {
            console.warn('Failed to save to cache:', error);
        }
    }

    public async validateSession(): Promise<void> {
        if (!this._cacheEnabled) return;
        try {
            const userId = await this.getCurrentUserId();
            if (userId) {
                await cacheManager.validateUserSession(userId);
            }
        } catch (error) {
            console.warn('Failed to validate cache for current user:', error);
            this._cacheEnabled = false;
        }
    }

    public setEnabled(enabled: boolean) {
        this._cacheEnabled = enabled;
    }

    public async invalidate(): Promise<void> {
        if (!this._cacheEnabled) return;
        const invalidationTask = this._pendingInvalidation
            .catch(() => undefined)
            .then(async () => {
                try {
                    const userId = await this.getCurrentUserId();
                    if (!userId) return;
                    const pattern = `${userId}_${this.context.getModelName()}_${this.context.getSyncHash()}_`;
                    await cacheManager.clear(pattern);
                } catch (error) {
                    console.warn('Failed to invalidate cache:', error);
                }
            });

        this._pendingInvalidation = invalidationTask.catch(() => undefined);
        await invalidationTask;
    }

    public reset() {
        this.hasDataFromCache = false;
        this.rawServerData = [];
    }

    public setRawData(data: T[]) {
        this.rawServerData = data;
    }

    public getRawData(): T[] {
        return this.rawServerData;
    }

    public upsertItem(item: T, toStart: boolean = false) {
        const updatedRaw = [...this.rawServerData];
        const rawIndex = updatedRaw.findIndex(row => (row as any).id === (item as any).id);
        if (rawIndex === -1) {
            toStart ? updatedRaw.unshift(item) : updatedRaw.push(item);
        } else {
            updatedRaw[rawIndex] = item;
        }
        this.rawServerData = updatedRaw;
    }

    public removeItem(id: string): boolean {
        const initialLength = this.rawServerData.length;
        this.rawServerData = this.rawServerData.filter(item => (item as any).id !== id);
        return this.rawServerData.length !== initialLength;
    }
}
